import { Lock, Pencil, Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { initialsOf, type MentionablePerson, type RemarkMention, type RemarkView } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { formatDateTime } from '../../../lib/format';
import type { RemarkDraft } from '../api/remarks-api';
import { RemarkComposer } from './RemarkComposer';
import { RemarkTags } from './RemarkTags';
import styles from './remarks.module.css';

// F23.13 (ADR 0049): a person's remarks, newest first — who, when (with the zone, non-negotiable #8), Team or
// Only me, tags and text with mentions picked out. Only the author edits or removes one.

interface RemarkThreadProps {
  readonly remarks: readonly RemarkView[];
  /** Absent where the viewer only reads (SHL-01). */
  readonly actions?: {
    readonly mentionable: readonly MentionablePerson[];
    readonly editingId: string | null;
    readonly onEditStart: (remark: RemarkView) => void;
    readonly onEditCancel: () => void;
    readonly onEditSave: (remark: RemarkView, draft: RemarkDraft) => void;
    readonly onRemove: (remark: RemarkView) => void;
  };
  readonly emptyText?: string;
}

export function RemarkThread({ remarks, actions, emptyText = 'No remarks yet.' }: RemarkThreadProps): ReactNode {
  if (remarks.length === 0) return <p className={styles.empty}>{emptyText}</p>;
  return (
    <ol className={styles.thread}>
      {remarks.map((remark) => (
        <li key={remark.id} className={styles.item} data-pending={remark.id.startsWith('pending-') || undefined}>
          <span className={styles.avatar} aria-hidden>
            {remark.isMine ? 'You' : initialsOf(remark.authorName)}
          </span>
          {actions && actions.editingId === remark.id ? (
            <RemarkComposer
              heading="Edit your remark"
              initial={{ tags: remark.tags, body: remark.body, visibility: remark.visibility, mentionUserIds: remark.mentions.map((mention) => mention.userId) }}
              mentionable={actions.mentionable}
              onSubmit={(draft) => actions.onEditSave(remark, draft)}
              onCancel={actions.onEditCancel}
              autoFocus
            />
          ) : (
            <div className={styles.itemBody}>
              <span className={styles.meta}>
                <span className={styles.author}>{remark.isMine ? 'You' : (remark.authorName ?? '—')}</span>
                <span>{formatDateTime(remark.at)}</span>
                {remark.visibility === 'private' && (
                  <span className={styles.private}>
                    <Lock size={12} aria-hidden /> Only me
                  </span>
                )}
                {remark.isEdited && <span>edited</span>}
                {actions && remark.isMine && !remark.id.startsWith('pending-') && (
                  <span className={styles.itemActions}>
                    <Button size="sm" variant="ghost" icon={Pencil} aria-label="Edit this remark" onClick={() => actions.onEditStart(remark)} />
                    <Button size="sm" variant="ghost" icon={Trash2} aria-label="Remove this remark" onClick={() => actions.onRemove(remark)} />
                  </span>
                )}
              </span>
              <RemarkTags tags={remark.tags} />
              {remark.body && <p className={styles.text}>{withMentions(remark.body, remark.mentions)}</p>}
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}

/** "@Nabil Bari" in the text, picked out where Nabil was mentioned. */
function withMentions(body: string, mentions: readonly RemarkMention[]): ReactNode {
  const names = mentions.map((mention) => `@${mention.name}`).filter((name) => body.includes(name));
  if (names.length === 0) return body;
  const pattern = new RegExp(`(${names.map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'g');
  return body.split(pattern).map((part, index) =>
    names.includes(part) ? (
      <span key={index} className={styles.mention}>
        {part}
      </span>
    ) : (
      part
    ),
  );
}
